import { Avatar } from '@/components/atoms/Avatar';
import Link from '@/components/atoms/legacy/Link';
import TextButton from '@/components/atoms/legacy/TextButton';
import Logo from '@/components/molecules/Logo';
import { SearchBarWithMenu } from '@/components/molecules/SearchBarWithMenu/SearchBarWithMenu';
import { useZipContext } from '@/context/ZipContext';
import { useSaveRecentSearchMutation } from '@/hooks/queries/recent-searches';
import { useAuth } from '@/hooks/useAuth';
import useWindowSize from '@/hooks/useWindowSize';
import { AFTER_LOGIN_REDIRECT_KEY, GOOGLE_TRACK_INFO } from '@/lib/constants';
import { handleFilteredSearch } from '@/lib/searchFilters';
import { getInitials } from '@/lib/user';
import { Component } from '@/types/interfaces';
import {
  Bars3BottomRightIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { signOut } from 'aws-amplify/auth';
import { useRouter } from 'next/compat/router';
import React from 'react';
import { DesktopHeaderLinks } from '../DesktopHeaderLinks/DesktopHeaderLinks';
import { MobileFooterLinks } from '../MobileFooterLinks/MobileFooterLink';
import { MobileHeaderLinks } from '../MobileHeaderLinks/MobileHeaderLinks';

const tailwind = {
  panel: [
    'absolute',
    'l:relative',
    'flex',
    'flex-col-reverse',
    'l:flex-row',
    'l:items-center',
    'l:justify-between',
    'l:h-auto',
    'gap-xl',
    'pb-[20px]',
    'l:p-0',
    'left-0',
    'right-0',
    'top-full',
    'z-40',
    'bg-white',
    'l:bg-transparent',
    'l:w-full',
    'overflow-auto',
  ].join(' '),
};

const NavBar: React.FC<Component> = ({ className = '', ...props }) => {
  const { user, evUser } = useAuth();
  const router = useRouter();
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [fadeStyle, setFadeStyle] = React.useState('hidden');
  const [searchInput, setSearchInput] = React.useState('');
  const [searchDialogOpen, setSearchDialogOpen] = React.useState(false);
  const { isDesktop } = useWindowSize();
  const userInitials = getInitials(evUser?.fullName);
  const { mutateAsync: saveRecentSearch } = useSaveRecentSearchMutation();
  const { preferredZip } = useZipContext();
  const searchContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (panelOpen || (searchDialogOpen && !isDesktop)) {
      document.documentElement.classList.add('overflow-hidden');
    }
    return () => {
      document.documentElement.classList.remove('overflow-hidden');
    };
  }, [panelOpen, searchDialogOpen, isDesktop]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDesktop &&
        searchDialogOpen &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setSearchDialogOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDesktop, searchDialogOpen]);
  React.useEffect(() => {
    const handleRouteChange = () => {
      setPanelOpen(false);
      setFadeStyle('fadeOut');
      setTimeout(() => {
        setFadeStyle('hidden');
      }, 300);
    };
    router?.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router?.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router?.events]);

  const { data: autoCompleteResults } = useQuery({
    queryKey: ['autoCompleteResults', user],
    queryFn: async () => {
      const response = await fetch('api/search/autocomplete-vehicles');
      if (!response.ok) {
        return { makes: [], models: {} };
      }
      const makesAndModels: {
        makes: string[];
        models: Record<string, string[]>;
      } = await response.json();

      return makesAndModels;
    },
    throwOnError: false,
    refetchOnWindowFocus: false,
    enabled: searchDialogOpen,
    staleTime: Infinity,
  });

  const getUpdatedPath = (path: string, input: string) => {
    const vectorSearchPattern = /\/vector_search\/[^/]+/;
    const sortPattern = /\/sort\/[^/]+/;

    const updatedPath = path.includes('/vector_search/')
      ? path.replace(vectorSearchPattern, `/vector_search/${input}`)
      : `${path}${path.endsWith('/') ? '' : '/'}vector_search/${input}`;

    return updatedPath.replace(sortPattern, '');
  };

  return (
    <>
      <header
        className={`relative flex items-center py-m ${className}`}
        {...props}
      >
        <div className="relative z-10 flex-grow l:mr-[80px]">
          <Link className="" href="/" aria-label="home">
            <Logo className={`relative w-[92px] flex-none l:w-[126px]`} />
          </Link>
        </div>

        <div className="relative z-50 flex flex-shrink-0 items-center gap-l lg:order-3">
          <div className="relative" ref={searchContainerRef}>
            <div
              className="flex cursor-pointer overflow-hidden rounded-t-xsmall border-b-[2px] border-white px-xs py-s hover:border-b-brandElectric/80 hover:bg-neutral-100"
              onClick={() => setSearchDialogOpen(true)}
            >
              <MagnifyingGlassIcon width={24} height={24} />
            </div>
            {isDesktop && searchDialogOpen && (
              <div className="absolute right-0 top-full z-[60] mt-s w-[500px]">
                <SearchBarWithMenu
                  input={searchInput}
                  limitSuggestions={false}
                  setInput={setSearchInput}
                  models={autoCompleteResults?.models}
                  makes={autoCompleteResults?.makes}
                  handleVectorSearch={async (input) => {
                    if (!input) return;
                    await saveRecentSearch(input);
                    const currentPath = router?.asPath;

                    if (currentPath?.includes('/search')) {
                      const updatedPath = getUpdatedPath(currentPath, input);
                      window.location.href = updatedPath;
                    } else {
                      handleFilteredSearch({
                        input,
                        zip: preferredZip,
                      });
                    }
                    setSearchDialogOpen(false);
                    setSearchInput('');
                  }}
                  setShowOptions={setSearchDialogOpen}
                  showOptions={searchDialogOpen}
                  searchBarVariant="section"
                />
              </div>
            )}
          </div>
          <Link
            className="hidden whitespace-nowrap rounded-full bg-brandElectric px-l py-s text-body1Medium text-brandMidnight hover:bg-brandElectric/90 ml:flex"
            href="https://partner.ev.com/#contact-form"
            analyticsEvent={GOOGLE_TRACK_INFO.partnerHomePageButton}
          >
            Become a partner
          </Link>
          {user ? (
            <Link
              className="flex items-center rounded-t-xsmall border-b-[2px] border-white px-xs py-s hover:border-b-brandElectric/80 hover:bg-neutral-100"
              activeClassName="!border-b-brandElectric"
              href="/my-evs"
            >
              <Avatar
                size={24}
                fallback={userInitials}
                imageIconSrc={user.picture}
              />
            </Link>
          ) : (
            <div className="flex flex-row gap-s">
              <Link
                className="group rounded-xsmall p-s text-body1Regular text-neutral-800 hover:bg-neutral-100 focus:bg-neutral-100"
                href="/login"
                analyticsEvent={GOOGLE_TRACK_INFO.loginDirectionButton}
                onClick={
                  router
                    ? () =>
                        sessionStorage.setItem(
                          AFTER_LOGIN_REDIRECT_KEY,
                          router.asPath
                        )
                    : undefined
                }
              >
                <div className="flex items-center gap-s">
                  <UserCircleIcon
                    width={24}
                    height={24}
                    className="focus:text-brandMidnight/80 group-hover:text-brandMidnight/80"
                  />
                </div>
              </Link>
            </div>
          )}
          <TextButton
            className={`ml-auto flex items-center justify-center hover:bg-neutral-100 ${
              panelOpen ? 'border-b-2 border-b-brandElectric' : ''
            } px-xs py-s l:hidden`}
            onClick={() =>
              setPanelOpen((prev) => {
                if (!prev) {
                  setFadeStyle('fadeIn');
                } else {
                  setFadeStyle('fadeOut');
                  setTimeout(() => {
                    setFadeStyle('hidden');
                  }, 300);
                }
                return !prev;
              })
            }
            aria-label={
              panelOpen ? 'Close navigation panel' : 'Open navigation panel'
            }
          >
            {panelOpen ? (
              <XMarkIcon
                width={24}
                height={24}
                className="text-brandMidnight"
              />
            ) : (
              <Bars3BottomRightIcon
                width={24}
                height={24}
                className="text-neutral-800"
              />
            )}
          </TextButton>
        </div>
        <section className={`${tailwind.panel} ${isDesktop ? '' : fadeStyle} `}>
          <section
            className={`order-2 hidden basis-4/5 px-l max-l:flex-col max-l:items-center max-l:space-y-xl m:px-[40px] l:order-1 l:block l:space-x-[40px]`}
          >
            <div className="flex-row gap-[40px] l:flex">
              <DesktopHeaderLinks />
            </div>
          </section>
          <section className="h-screen gap-l overflow-auto l:hidden">
            {panelOpen && <MobileHeaderLinks user={user} signOut={signOut} />}
            {!isDesktop && panelOpen && !!router && (
              <MobileFooterLinks router={router} />
            )}
          </section>
        </section>
      </header>
      {!isDesktop && (
        <div
          className={`fixed inset-0 h-screen w-full ${searchDialogOpen ? 'block' : 'hidden'} z-[100] bg-neutral-100`}
        >
          <SearchBarWithMenu
            input={searchInput}
            limitSuggestions={false}
            setInput={setSearchInput}
            models={autoCompleteResults?.models}
            makes={autoCompleteResults?.makes}
            handleVectorSearch={async (input) => {
              if (!input) return;
              await saveRecentSearch(input);
              const currentPath = router?.asPath;

              if (currentPath?.includes('/search')) {
                const updatedPath = getUpdatedPath(currentPath, input);
                window.location.href = updatedPath;
              } else {
                handleFilteredSearch({
                  input,
                  zip: preferredZip,
                });
              }
              setSearchDialogOpen(false);
              setSearchInput('');
            }}
            setShowOptions={setSearchDialogOpen}
            showOptions={searchDialogOpen}
            searchBarVariant="dialog"
          />
        </div>
      )}
    </>
  );
};

export default NavBar;
